import React, { useEffect, useState } from 'react';
import { Card, Button, Input } from '../components/UI';
import { apiRequest } from '../utils/api';
import { RuleItem, RuleGroup } from '../types';
import { useLeague } from '../components/LeagueContext';
import { RefreshCw, Plus, Edit2, Trash2, X, Save, BookOpen, AlertTriangle } from 'lucide-react';

export const RulesAdmin: React.FC = () => {
    const { refreshData, rules } = useLeague();
    const [editingRule, setEditingRule] = useState<Partial<RuleItem> & { group_name?: string } | null>(null);
    const [isCreating, setIsCreating] = useState(false);
    const [loading, setLoading] = useState(false);

    // Filter state
    const [filterGroup, setFilterGroup] = useState<string>('ALL');

    const handleSave = async () => {
        if (!editingRule || !editingRule.label || !editingRule.id || !editingRule.group_name) {
            alert("Bitte alle Pflichtfelder ausfüllen (Gruppe, ID, Beschreibung).");
            return;
        }

        setLoading(true);
        try {
            const payload = {
                id: editingRule.db_id, // ID for update
                group_name: editingRule.group_name,
                rule_key: editingRule.id, // mapped to rule_key in DB
                description: editingRule.label,
                points: editingRule.points || 0,
                extra: editingRule.extra || []
            };

            if (editingRule.db_id) {
                await apiRequest('rules.php', 'PUT', payload);
            } else {
                await apiRequest('rules.php', 'POST', payload);
            }
            await refreshData();
            setEditingRule(null);
            setIsCreating(false);
        } catch (e: any) {
            alert("Fehler: " + e.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (dbId: number) => {
        if (!confirm("Regel wirklich löschen?")) return;
        try {
            await apiRequest(`rules.php?id=${dbId}`, 'DELETE');
            await refreshData();
        } catch (e: any) {
            alert(e.message);
        }
    };

    const startEdit = (rule: RuleItem, group: string) => {
        setEditingRule({
            ...rule,
            group_name: group
        });
        setIsCreating(false);
    };

    const startCreate = () => {
        setEditingRule({
            id: '',
            label: '',
            points: 0,
            extra: [],
            group_name: filterGroup !== 'ALL' ? filterGroup : ''
        });
        setIsCreating(true);
    };

    const handleExtraChange = (val: string) => {
        // Split by comma and clean
        const arr = val.split(',').map(s => s.trim()).filter(s => s !== '');
        setEditingRule(prev => prev ? ({ ...prev, extra: arr }) : null);
    };

    const allGroups = Array.from(new Set(rules.map(r => r.group)));

    return (
        <div className="space-y-6 animate-fade-in pb-20">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold uppercase italic">Regelwerk Verwaltung</h1>
                    <p className="text-sm text-gray-400">Strafenkatalog & Regeln bearbeiten</p>
                </div>
                <div className="flex gap-2">
                    <Button variant="secondary" icon={RefreshCw} onClick={refreshData}>Refresh</Button>
                    <Button icon={Plus} onClick={startCreate}>Neue Regel</Button>
                </div>
            </div>

            {/* Filter */}
            <div className="flex gap-2 overflow-x-auto pb-2">
                <button 
                    onClick={() => setFilterGroup('ALL')}
                    className={`px-3 py-1 rounded text-xs font-bold uppercase whitespace-nowrap transition-colors ${filterGroup === 'ALL' ? 'bg-f1-red text-white' : 'bg-gray-800 text-gray-400 hover:text-white'}`}
                >
                    Alle
                </button>
                {allGroups.map(g => (
                    <button 
                        key={g}
                        onClick={() => setFilterGroup(g)}
                        className={`px-3 py-1 rounded text-xs font-bold uppercase whitespace-nowrap transition-colors ${filterGroup === g ? 'bg-f1-red text-white' : 'bg-gray-800 text-gray-400 hover:text-white'}`}
                    >
                        {g}
                    </button>
                ))}
            </div>

            <div className="space-y-6">
                {rules.filter(g => filterGroup === 'ALL' || g.group === filterGroup).map(group => (
                    <Card key={group.group} className="border-l-4 border-l-gray-600">
                        <h3 className="text-lg font-bold text-white uppercase mb-4 flex items-center gap-2">
                            <BookOpen size={18} className="text-gray-400"/> {group.group}
                        </h3>
                        <div className="space-y-2">
                            {group.items.map(rule => (
                                <div key={rule.db_id} className="bg-gray-900/50 p-3 rounded border border-gray-800 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 hover:border-gray-600 transition-colors">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="font-mono text-f1-red font-bold text-sm bg-f1-red/10 px-2 py-0.5 rounded border border-f1-red/20">{rule.id}</span>
                                            {rule.points !== 0 && (
                                                <span className={`text-xs font-bold px-2 py-0.5 rounded ${rule.points! < 0 ? 'bg-red-900/40 text-red-400' : 'bg-green-900/40 text-green-400'}`}>
                                                    {rule.points} Pkt
                                                </span>
                                            )}
                                            {rule.extra.map((ex, i) => (
                                                <span key={i} className="text-[10px] uppercase font-bold bg-yellow-900/30 text-yellow-500 border border-yellow-700/50 px-1.5 py-0.5 rounded">
                                                    {ex}
                                                </span>
                                            ))}
                                        </div>
                                        <div className="text-sm text-gray-300">{rule.label}</div>
                                    </div>
                                    <div className="flex gap-2">
                                        <button onClick={() => startEdit(rule, group.group)} className="p-1.5 bg-gray-800 rounded text-gray-400 hover:text-white hover:bg-gray-700"><Edit2 size={14}/></button>
                                        <button onClick={() => handleDelete(rule.db_id!)} className="p-1.5 bg-gray-800 rounded text-gray-400 hover:text-red-500 hover:bg-red-900/20"><Trash2 size={14}/></button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Card>
                ))}
                {rules.length === 0 && <div className="text-center p-10 text-gray-500 italic">Keine Regeln gefunden.</div>}
            </div>

            {/* Edit Modal */}
            {(editingRule || isCreating) && (
                <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
                    <Card className="w-full max-w-lg bg-f1-dark border-gray-600">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-xl font-bold uppercase text-white">{isCreating ? 'Neue Regel' : 'Regel bearbeiten'}</h3>
                            <button onClick={() => { setEditingRule(null); setIsCreating(false); }}><X className="text-gray-400 hover:text-white"/></button>
                        </div>
                        
                        <div className="space-y-4">
                            <div>
                                <label className="block text-gray-400 text-xs font-bold mb-2 uppercase">Gruppe (Paragraph)</label>
                                <div className="flex gap-2">
                                    <input 
                                        className="w-full bg-gray-900 border border-gray-700 rounded p-2 text-white list-none"
                                        list="group-suggestions"
                                        value={editingRule?.group_name || ''}
                                        onChange={e => setEditingRule(prev => prev ? ({...prev, group_name: e.target.value}) : null)}
                                        placeholder="z.B. §3 Anmeldung"
                                    />
                                    <datalist id="group-suggestions">
                                        {allGroups.map(g => <option key={g} value={g}/>)}
                                    </datalist>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <Input 
                                    label="Regel ID (z.B. 3.7)" 
                                    value={editingRule?.id || ''} 
                                    onChange={e => setEditingRule(prev => prev ? ({...prev, id: e.target.value}) : null)}
                                />
                                <Input 
                                    label="Strafpunkte (Negativ = Strafe)" 
                                    type="number"
                                    value={editingRule?.points || 0} 
                                    onChange={e => setEditingRule(prev => prev ? ({...prev, points: parseInt(e.target.value)}) : null)}
                                />
                            </div>

                            <Input 
                                label="Beschreibung / Tatbestand" 
                                value={editingRule?.label || ''} 
                                onChange={e => setEditingRule(prev => prev ? ({...prev, label: e.target.value}) : null)}
                            />

                            <div>
                                <label className="block text-gray-400 text-xs font-bold mb-2 uppercase">Zusatz-Sanktionen (Kommagetrennt)</label>
                                <input 
                                    className="w-full bg-gray-900 border border-gray-700 rounded p-2 text-white"
                                    value={editingRule?.extra?.join(', ') || ''}
                                    onChange={e => handleExtraChange(e.target.value)}
                                    placeholder="z.B. Qualisperre, Liga-Ausschluss"
                                />
                                <p className="text-[10px] text-gray-500 mt-1">Mehrere Werte mit Komma trennen.</p>
                            </div>
                        </div>

                        <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-gray-700">
                            <Button variant="ghost" onClick={() => { setEditingRule(null); setIsCreating(false); }}>Abbrechen</Button>
                            <Button onClick={handleSave} disabled={loading} icon={Save}>Speichern</Button>
                        </div>
                    </Card>
                </div>
            )}
        </div>
    );
};